import { VerticalDotsMenuIcon } from "icons";
import React, { useState } from "react";
import { Avatar, Popover, Text, TextButton } from "ui-components";

import { DeleteConversationConfirmationModal } from "@/src/components/modals/DeleteConversationConfirmationModal";
import ReportAndBlockModal from "@/src/components/modals/ReportAndBlockModal";
import { useMessageMarkConversationAsUnread } from "@/src/hooks/api/generated";
import { useChat } from "@/src/hooks/useChat";
import { useChatFunctions } from "@/src/hooks/useChatFunctions";

import { ChatBoxSearchField } from "./ChatBoxSearchField";

export interface IChatBoxHeaderProps {
  isLoading: boolean;
  onSearchTermChange: (text: string) => void;
}

export function ChatBoxHeader({
  isLoading,
  onSearchTermChange,
}: IChatBoxHeaderProps): JSX.Element {
  const { chatMetaData, selectedConversation } = useChat();
  const { markConversationAsBlocked, toggleConversationReadStatus } =
    useChatFunctions();
  const [isOpenOptionMenu, setIsOpenOptionMenu] = useState(false);
  const [isShowBlockUser, setIsShowBlockUser] = useState(false);
  const [isOpenReportAndBlockModal, setIsOpenReportAndBlockModal] =
    useState(false);
  const [
    isOpenDeleteChatConfirmationModal,
    setIsOpenDeleteChatConfirmationModal,
  ] = useState(false);

  const { mutate: markConversationAsUnread } =
    useMessageMarkConversationAsUnread();

  const handleMarkConversationUnread = (): void => {
    if (!selectedConversation?.conversationId) {
      return;
    }
    markConversationAsUnread(
      {
        conversationId: selectedConversation?.conversationId?.toString(),
      },
      {
        onSuccess: () => {
          if (selectedConversation?.conversationId) {
            toggleConversationReadStatus(
              selectedConversation?.conversationId,
              true
            );
          }
        },
      }
    );
  };

  return (
    <div className="flex border-b p-5 relative items-center justify-between h-[70px] ">
      <DeleteConversationConfirmationModal
        isOpen={isOpenDeleteChatConfirmationModal}
        onOpenChange={setIsOpenDeleteChatConfirmationModal}
      />
      <ReportAndBlockModal
        isOpen={isOpenReportAndBlockModal}
        isShowBlock={isShowBlockUser}
        closeButtonTitle="Back to my messages"
        onOpenChange={setIsOpenReportAndBlockModal}
        userId={selectedConversation?.userId}
        onUserBlockSuccessfully={(isBlockedSuccessFully) => {
          if (
            isBlockedSuccessFully &&
            selectedConversation &&
            selectedConversation.conversationId
          ) {
            markConversationAsBlocked(selectedConversation?.conversationId);
          }
        }}
      />
      {chatMetaData.isSearchConnectionOpen ? (
        <ChatBoxSearchField
          className="border-none"
          placeholder="Type your connection’s name"
          onChange={onSearchTermChange}
          isLoading={isLoading}
        />
      ) : (
        <div className="flex flx-1 w-full justify-between flex-row gap-4">
          <div className="flex flex-row gap-4">
            <Avatar
              alt=""
              size="xxs"
              src={selectedConversation?.logoSrc}
              status={selectedConversation?.userStatus}
            />
            <div>
              <div className="flex gap-2">
                <Text variant="base-bold">{selectedConversation?.title}</Text>
                <Text variant="base-thin">
                  {selectedConversation?.userPronounce}
                </Text>
              </div>
              <Text>{selectedConversation?.subtitle}</Text>
            </div>
          </div>
          <Popover isOpen={isOpenOptionMenu} onOpenChange={setIsOpenOptionMenu}>
            <Popover.Trigger>
              <VerticalDotsMenuIcon
                className={`w-1.5 text-brand-dark-gray ${
                  selectedConversation?.conversationId ? "visible" : "hidden"
                }`}
              />
            </Popover.Trigger>
            <Popover.Content
              offset={-10}
              placement="bottom right"
              className="rounded-2xl p-2 z-10"
            >
              {selectedConversation?.isBlocked ? (
                <div className="flex flex-col gap-2">
                  <TextButton
                    onPress={() => {
                      setIsOpenOptionMenu(false);
                      setIsOpenDeleteChatConfirmationModal(
                        !isOpenDeleteChatConfirmationModal
                      );
                    }}
                  >
                    Delete Conversation
                  </TextButton>
                </div>
              ) : (
                <div className="flex flex-col gap-2">
                  <TextButton
                    onPress={() => {
                      setIsOpenOptionMenu(false);
                      handleMarkConversationUnread();
                    }}
                  >
                    Mark as Unread
                  </TextButton>
                  <TextButton
                    onPress={() => {
                      setIsOpenOptionMenu(false);
                      setIsOpenDeleteChatConfirmationModal(
                        !isOpenDeleteChatConfirmationModal
                      );
                    }}
                  >
                    Delete Conversation
                  </TextButton>
                  <TextButton
                    onPress={() => {
                      setIsOpenOptionMenu(false);
                      setIsShowBlockUser(true);
                      setIsOpenReportAndBlockModal(!isOpenReportAndBlockModal);
                    }}
                  >
                    Block
                  </TextButton>
                  <TextButton
                    onPress={() => {
                      setIsOpenOptionMenu(false);
                      setIsShowBlockUser(false);
                      setIsOpenReportAndBlockModal(!isOpenReportAndBlockModal);
                    }}
                  >
                    Report
                  </TextButton>
                </div>
              )}
            </Popover.Content>
          </Popover>
        </div>
      )}
    </div>
  );
}
